import {
  hasPassedDragThreshold,
  shouldCaptureEventDragStart,
} from '../lib/dragSelect';
import type { RefObject } from 'react';
import { useEffect } from 'react';

const ABSENCE_BAR = '.absence-event';

/**
 * Where a substitute mousedown may land so FullCalendar accepts it as the start
 * of a date selection. The event harness is the usual answer -- it wraps a bar
 * without being one -- and the lane elements cover the gap between bars.
 */
function findDateDownTarget(
  root: HTMLElement,
  clientX: number,
  clientY: number,
): HTMLElement | null {
  for (const element of document.elementsFromPoint(clientX, clientY)) {
    if (!(element instanceof HTMLElement)) continue;
    if (!root.contains(element)) continue;
    if (element.closest(ABSENCE_BAR)) continue;
    return element;
  }
  return null;
}

function dispatchMouseEvent(
  target: HTMLElement,
  type: 'mousedown' | 'mousemove',
  clientX: number,
  clientY: number,
) {
  const event = new MouseEvent(type, {
    bubbles: true,
    cancelable: true,
    view: window,
    button: 0,
    buttons: 1,
    clientX,
    clientY,
  });
  // FullCalendar's pointer tracking reads pageX/pageY. Browsers derive those
  // from clientX/clientY for constructed events, but the spec leaves it to the
  // implementation, so pin them rather than risk a drag anchored at 0,0.
  Object.defineProperty(event, 'pageX', { value: clientX + window.scrollX });
  Object.defineProperty(event, 'pageY', { value: clientY + window.scrollY });
  target.dispatchEvent(event);
}

/**
 * Let a range selection begin on top of an absence bar.
 *
 * FullCalendar checks the mousedown target before it will start a date
 * selection and rejects anything inside a foreground event
 * (`isValidDateDownEl`), so pressing on an absence and sweeping across days
 * does nothing. Only that first target is checked -- once a drag is running,
 * hits are resolved from cached geometry -- so it is enough to catch the press
 * ourselves and, once the pointer has actually moved, replay it against the
 * lane underneath. From there FullCalendar runs the whole gesture: the
 * highlight, `selectAllow`, and a single `select` on release.
 *
 * The replay waits for movement so a press that never becomes a drag is left
 * untouched and still opens the details pane through `eventClick`.
 */
export function useEventDragSelect(
  calendarFrameRef: RefObject<HTMLDivElement | null>,
  isMonthView: boolean,
) {
  useEffect(() => {
    const root = calendarFrameRef.current;
    if (!root) {
      return;
    }

    let origin: { x: number; y: number } | null = null;
    let handedOver = false;
    let suppressClick = false;
    let suppressTimer: ReturnType<typeof setTimeout> | null = null;

    const disarmClickSuppression = () => {
      suppressClick = false;
      if (suppressTimer !== null) {
        clearTimeout(suppressTimer);
        suppressTimer = null;
      }
    };

    /**
     * Drop the click that follows a drag which ended back on the bar it started
     * on. Left alone it would open the details pane over whatever the selection
     * just opened.
     *
     * The listener lives for as long as the hook does and reads a flag, rather
     * than being armed per gesture: a release outside the calendar produces a
     * click on a shared ancestor instead, so a listener scoped to the frame
     * would stay armed and swallow some later, unrelated click. Watching the
     * document sees that click too and disarms on it -- while only suppressing
     * clicks that actually landed in the calendar, so a release over the
     * sidebar still reaches whatever it hit.
     */
    const handleClickCapture = (event: MouseEvent) => {
      if (!suppressClick) return;
      disarmClickSuppression();

      const target = event.target;
      if (target instanceof Node && root.contains(target)) {
        event.stopPropagation();
      }
    };

    const stopTracking = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      origin = null;
    };

    function handleMouseMove(event: MouseEvent) {
      if (!origin || handedOver) return;
      const point = { x: event.clientX, y: event.clientY };
      if (!hasPassedDragThreshold(origin, point)) return;

      const frame = calendarFrameRef.current;
      const target = frame
        ? findDateDownTarget(frame, origin.x, origin.y)
        : null;
      if (!target) {
        // Nothing to hand the press to -- leave the gesture as a plain click.
        stopTracking();
        return;
      }

      handedOver = true;
      dispatchMouseEvent(target, 'mousedown', origin.x, origin.y);
      // Catch the selection up to where the pointer already is instead of
      // waiting for the next real move.
      dispatchMouseEvent(target, 'mousemove', point.x, point.y);
      document.removeEventListener('mousemove', handleMouseMove);
    }

    function handleMouseUp() {
      if (handedOver) {
        suppressClick = true;
        // A release outside the browser window can end the drag without any
        // click at all, so the flag must not outlive this gesture on the
        // strength of one arriving. Whichever comes first disarms it.
        suppressTimer = setTimeout(disarmClickSuppression, 0);
      }
      handedOver = false;
      stopTracking();
    }

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      const bar = target.closest<HTMLElement>(ABSENCE_BAR);
      const capture = shouldCaptureEventDragStart({
        button: event.button,
        ctrlKey: event.ctrlKey,
        inTimelineLane: Boolean(target.closest('.absence-resource-lane')),
        isMonthView,
        onEvent: Boolean(bar),
        onResizer: Boolean(target.closest('.absence-event-resizer')),
        onTransient: Boolean(bar?.classList.contains('transient-event')),
      });
      if (!capture) return;

      // Keep the press away from FullCalendar: all it would do is mark the
      // gesture unselectable. `preventDefault` suppresses text selection, which
      // FullCalendar does for every press in the lane body anyway.
      event.stopPropagation();
      event.preventDefault();

      disarmClickSuppression();
      origin = { x: event.clientX, y: event.clientY };
      handedOver = false;
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    };

    root.addEventListener('mousedown', handleMouseDown, true);
    document.addEventListener('click', handleClickCapture, true);

    return () => {
      root.removeEventListener('mousedown', handleMouseDown, true);
      document.removeEventListener('click', handleClickCapture, true);
      disarmClickSuppression();
      stopTracking();
    };
  }, [calendarFrameRef, isMonthView]);
}
